// "Running low" chips (ux/05 Restock): things this household buys regularly
// that are due again and not on the list.
//
// The history is the list itself. Finish trip soft-deletes the basket
// (finish_trip), so every row with done = true, a done_at and a
// deleted_at is one past purchase — no history table. Names are compared
// with nameKey (lib/normalize.ts), the one definition of "same ingredient",
// so "Ägg" bought last week and "egg" on the list today are the same thing.
//
// One trip is one purchase: rows of the same thing ticked within
// STALE_BASKET_MS of each other (two "milk", "Ägg" and "eggs", Add both) are
// one, or the gap between them would make it look due every few minutes. The
// order puts a regular habit that is due ahead of a rare thing that is very
// overdue, and a thing not bought for three times its interval is dropped.

import { RESTOCK, STALE_BASKET_MS } from './constants'
import { itemKey } from './normalize'
import type { Item } from './supabase'

export type Purchase = { name: string; categoryId: string | null; doneAt: Date }
export type RestockChip = { key: string; name: string; categoryId: string | null; hint: string; overdue: number }

const DAY = 86_400_000
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

function hint(intervalDays: number, sinceDays: number) {
  if (sinceDays / intervalDays > RESTOCK.overdueFactor) return `${Math.round(sinceDays)} days ago`
  if (intervalDays >= 6 && intervalDays <= 8) return 'Weekly'
  if (intervalDays >= 12 && Math.abs(intervalDays / 7 - Math.round(intervalDays / 7)) < 0.2) return `Every ${Math.round(intervalDays / 7)} weeks`
  return `Every ${Math.round(intervalDays)} days`
}

/** Past purchases from soft-deleted rows: ticked, then cleared by Finish trip. */
export function purchasesFrom(rows: Pick<Item, 'name' | 'category_id' | 'done' | 'done_at' | 'deleted_at'>[]): Purchase[] {
  return rows
    .filter(r => r.done && r.done_at && r.deleted_at)
    .map(r => ({ name: r.name, categoryId: r.category_id, doneAt: new Date(r.done_at!) }))
}

/** The purchase times with those of one trip merged, oldest first. */
export function tripTimes(times: Date[]): number[] {
  const sorted = times.map(t => t.getTime()).sort((a, b) => a - b)
  const trips: number[] = []
  for (const t of sorted) if (!trips.length || t - trips[trips.length - 1] > STALE_BASKET_MS) trips.push(t)
  return trips
}

/** How many trips bought it (tripTimes). */
export const tripCount = (times: Date[]) => tripTimes(times).length

/**
 * @param history  past purchases across finished trips
 * @param onList   names currently active or in the basket
 */
export function computeRestock(history: Purchase[], onList: string[], now: Date, opts: { max?: number } = {}): RestockChip[] {
  const excluded = new Set(onList.map(itemKey))
  const byName = new Map<string, Purchase[]>()
  for (const p of history) {
    const key = itemKey(p.name)
    if (!key || excluded.has(key)) continue
    byName.set(key, [...(byName.get(key) ?? []), p])
  }

  const scored: (RestockChip & { score: number })[] = []
  for (const [key, purchases] of byName) {
    const times = tripTimes(purchases.map(p => p.doneAt))
    const n = times.length
    if (n < RESTOCK.minPurchases) continue
    const interval = median(times.slice(1).map((t, i) => (t - times[i]) / DAY))
    const last = purchases.reduce((a, b) => (a.doneAt > b.doneAt ? a : b))
    const since = (now.getTime() - last.doneAt.getTime()) / DAY
    if (interval <= 0 || since < RESTOCK.dueFactor * interval || since > RESTOCK.staleFactor * interval) continue
    const overdue = since / interval
    const score = Math.min(overdue, RESTOCK.urgencyCap) * n / (n + RESTOCK.regularityPrior)
    scored.push({ key, name: last.name, categoryId: last.categoryId, hint: hint(interval, since), overdue, score })
  }
  return scored
    .sort((a, b) => b.score - a.score || b.overdue - a.overdue || a.key.localeCompare(b.key))
    .slice(0, opts.max ?? RESTOCK.max)
    .map(({ score: _score, ...chip }) => chip)
}
